import {
  cloneDefaultMegastructureVisualization,
  validateMegastructureVisualization,
} from "./stackedMegastructurePreview.js";

const ARTIFACT_KIND = "stacked_megastructure_visualization";

export function newestFirstMegastructureRevisions(revisions) {
  return [...revisions].sort((a, b) => b.revisionNumber - a.revisionNumber);
}

export function stepMegastructureParameter(value, direction, { min, max, step }) {
  return normalizeMegastructureParameter(Number(value) + Number(direction) * Number(step), { min, max, step });
}

export function normalizeMegastructureParameter(value, { min, max, step }) {
  if (value === "" || value == null || !Number.isFinite(Number(value))) return null;
  const numericMin = Number(min);
  const numericMax = Number(max);
  const numericStep = Number(step);
  const clamped = Math.min(numericMax, Math.max(numericMin, Number(value)));
  const snapped = numericMin + Math.round((clamped - numericMin) / numericStep) * numericStep;
  const precision = Math.max(0, String(step).split(".")[1]?.length || 0);
  return Number(Math.min(numericMax, Math.max(numericMin, snapped)).toFixed(precision));
}

export function describeMegastructureLiveStatus({ sourceMode, phase, savedLabel, baseLabel, pendingBaseLabel, draftCount }) {
  if (sourceMode === "blender") return {
    tone: "reference",
    label: "BLENDER REFERENCE",
    detailText: `Read-only export. Apply live returns to ${baseLabel}.`,
  };
  if (phase === "thinking") return {
    tone: "working",
    label: "LLM APPLYING…",
    detailText: `Command is based on ${pendingBaseLabel}. The viewport still shows that base.`,
  };
  if (phase === "rendering") return {
    tone: "working",
    label: "LIVE · APPLYING…",
    detailText: `The command was accepted and is being rendered from ${pendingBaseLabel}.`,
  };
  if (phase === "saving") return {
    tone: "working",
    label: "LIVE · RENDERED · SAVING…",
    detailText: "The new model is already in the viewport; its revision is being saved.",
  };
  if (phase === "save_error") return {
    tone: "error",
    label: "LIVE · NOT SAVED",
    detailText: "The viewport has the latest model, but saving failed. Retry before navigating away.",
  };
  if (phase === "command_error") return {
    tone: "error",
    label: "COMMAND NOT APPLIED",
    detailText: `The viewport still shows ${baseLabel}; interpretation or validation failed before saving.`,
  };
  if (phase === "offline_error") return {
    tone: "error",
    label: "LIVE HISTORY OFFLINE",
    detailText: "Showing the local Blender 5.2 specification; no remote revision is loaded.",
  };
  if (draftCount) return {
    tone: "draft",
    label: `LIVE DRAFT · ${draftCount} UNSAVED`,
    detailText: `The viewport is newer than ${savedLabel}. Save version to keep it.`,
  };
  return {
    tone: "saved",
    label: `LIVE · SAVED ${savedLabel}`,
    detailText: "The viewport matches this saved revision.",
  };
}

export function describeMegastructureDraftSave(draftParts) {
  const count = draftParts.length;
  return {
    disabled: count === 0,
    label: count ? `${count} part${count === 1 ? "" : "s"} changed` : "No unsaved edits",
  };
}

const FIELDS = [
  ["landscape", "Ground width", "landscape.widthM", 100, 100_000, 100, "m"],
  ["landscape", "Ground depth", "landscape.depthM", 100, 100_000, 100, "m"],
  ["landscape", "Ground thickness", "landscape.thicknessM", 0.1, 1_000, 0.1, "m"],
  ["tier_stack", "Tier count", "tiers.count", 1, 100, 1, ""],
  ["tier_stack", "Vertical pitch", "tiers.verticalPitchM", 10, 10_000, 10, "m"],
  ["disk", "Disk diameter", "tiers.disk.diameterM", 10, 20_000, 10, "m"],
  ["disk", "Disk height", "tiers.disk.heightM", 1, 2_000, 1, "m"],
  ["perimeter_wall", "Wall thickness", "tiers.perimeterWall.radialThicknessM", 0.1, 2_000, 0.1, "m"],
  ["perimeter_wall", "Wall height", "tiers.perimeterWall.heightM", 1, 5_000, 1, "m"],
  ["central_pillar", "Pillar diameter", "tiers.centralPillar.diameterM", 1, 5_000, 1, "m"],
  ["central_pillar", "Pillar height", "tiers.centralPillar.heightM", 1, 10_000, 1, "m"],
  ["structure_array", "Sky elevators", "structureArray.instanceCount", 1, 64, 1, ""],
  ["structure_array", "Elevator spacing", "structureArray.spacingM", 3_500, 100_000, 100, "m"],
  ["structure_array", "Array spread", "structureArray.spreadDiameterM", 0, 100_000, 100, "m"],
  ["structure_array", "Randomness", "structureArray.randomness", 0, 1, 0.05, ""],
  ["structure_array", "Seed", "structureArray.seed", 0, 2_147_483_647, 1, ""],
  ["structure_array", "Min clearance", "structureArray.minimumClearanceM", 0, 10_000, 100, "m"],
  ["structure_array", "Ground padding", "structureArray.groundPaddingM", 0, 10_000, 100, "m"],
];

/** Switch input ownership without moving or reframing the current camera. */
export function enterMegastructureFlight(viewer) {
  viewer.setFlyMode(true);
}

function getPath(object, path) {
  return path.split(".").reduce((value, key) => value[key], object);
}

function setPath(object, path, value) {
  const segments = path.split(".");
  let cursor = object;
  for (const segment of segments.slice(0, -1)) cursor = cursor[segment];
  cursor[segments.at(-1)] = value;
}

function changedParts(before, after) {
  const parts = new Set();
  for (const [part, , path] of FIELDS) if (getPath(before, path) !== getPath(after, path)) parts.add(part);
  if (JSON.stringify(before.appearance) !== JSON.stringify(after.appearance)) parts.add("appearance");
  if (JSON.stringify(before.additions || []) !== JSON.stringify(after.additions || [])) parts.add("additions");
  return [...parts];
}

export function initMegastructureStudio({
  viewer,
  api,
  toast,
  esc,
  getProjectId,
  onRevisionChange,
  setStats,
}) {
  const state = {
    active: false,
    loading: false,
    artifact: null,
    revisions: [],
    visibleRevision: null,
    scene: cloneDefaultMegastructureVisualization(),
    baseline: cloneDefaultMegastructureVisualization(),
    sourceMode: "live",
    phase: "saved",
    draftParts: [],
    renderedScene: null,
    operationId: 0,
    pendingBaseLabel: "",
    renderQueued: false,
    firstLoad: true,
    cameraPose: null,
  };

  const sidebar = document.querySelector("#sidebar");
  const detail = document.querySelector("#detail");
  const detailBody = document.querySelector("#detail-body");
  const dropHint = document.querySelector("#drop-hint");
  const viewport = document.querySelector("#viewport");

  function revisionLabel(revision) {
    return revision ? `R${String(revision.revisionNumber).padStart(2, "0")}` : "SOURCE";
  }

  function revisionById(id) {
    return state.revisions.find((revision) => revision.id === id) || null;
  }

  function isBusy() {
    return state.phase === "thinking" || state.phase === "rendering" || state.phase === "saving";
  }

  function visibleBaseLabel() {
    const saved = revisionLabel(state.visibleRevision);
    return state.draftParts.length
      ? `${saved} + ${state.draftParts.length} unsaved part${state.draftParts.length === 1 ? "" : "s"}`
      : saved;
  }

  function updateLiveStatus() {
    const { tone, label, detailText } = describeMegastructureLiveStatus({
      sourceMode: state.sourceMode,
      phase: state.phase,
      savedLabel: revisionLabel(state.visibleRevision),
      baseLabel: visibleBaseLabel(),
      pendingBaseLabel: state.pendingBaseLabel,
      draftCount: state.draftParts.length,
    });
    const host = detailBody.querySelector("[data-live-status]");
    if (host) {
      host.dataset.tone = tone;
      host.querySelector("b").textContent = label;
      host.querySelector("span").textContent = detailText;
    }
    if (viewport) {
      viewport.dataset.liveTone = tone;
      viewport.dataset.liveLabel = label;
    }
    const composerNote = detailBody.querySelector(".mega-composer small");
    if (composerNote) composerNote.textContent = state.sourceMode === "blender"
      ? `Reference only · command will use ${visibleBaseLabel()}`
      : state.phase === "thinking" || state.phase === "rendering"
        ? `Locked to ${state.pendingBaseLabel}`
        : `Next command starts from ${visibleBaseLabel()}, exactly as rendered`;
    detailBody.querySelectorAll("[data-history-revision], [data-source], [data-mega-number], [data-mega-range], [data-mega-step], [data-mega-colour], [data-mega-addition], [data-focus-context], [data-focus-array], [data-remove-addition], [data-save-draft]")
      .forEach((control) => {
        if (isBusy()) {
          if (control.dataset.liveDisabledBefore == null) control.dataset.liveDisabledBefore = control.disabled ? "1" : "0";
          control.disabled = true;
        } else if (control.dataset.liveDisabledBefore != null) {
          control.disabled = control.dataset.liveDisabledBefore === "1";
          delete control.dataset.liveDisabledBefore;
        }
      });
  }

  function renderLiveNow(scene = state.scene, { keepCamera = true } = {}) {
    if (!state.active || state.sourceMode !== "live") return;
    state.scene = validateMegastructureVisualization(scene);
    setStats(viewer.loadStackedMegastructure(state.scene, { keepCamera: keepCamera && !state.firstLoad }));
    state.renderedScene = structuredClone(state.scene);
    if (state.firstLoad && state.cameraPose) viewer.restoreCameraPose(state.cameraPose);
    state.firstLoad = false;
    dropHint.style.display = "none";
    detailBody.querySelector("[data-visualization-error]")?.replaceChildren();
    updateLiveStatus();
  }

  function queueLiveRender({ keepCamera = true } = {}) {
    if (!state.active || state.sourceMode !== "live" || state.renderQueued) return;
    state.renderQueued = true;
    requestAnimationFrame(() => {
      state.renderQueued = false;
      try {
        renderLiveNow(state.scene, { keepCamera });
      } catch (error) {
        const target = detailBody.querySelector("[data-visualization-error]");
        if (target) target.textContent = String(error.message || error);
      }
    });
  }

  async function showBlenderExport() {
    state.sourceMode = "blender";
    renderSourceControls();
    updateLiveStatus();
    try {
      setStats(await viewer.load(state.scene.edit.blenderSource.glbUrl, { keepCamera: !state.firstLoad }));
      state.firstLoad = false;
      dropHint.style.display = "none";
    } catch (error) {
      toast(`Blender 5.2 export could not be loaded: ${String(error)}`, true);
    }
  }

  function showLiveProxy() {
    state.sourceMode = "live";
    renderSourceControls();
    renderLiveNow(state.scene, { keepCamera: true });
  }

  function renderSourceControls() {
    const host = detailBody.querySelector("[data-source-switch]");
    if (!host) return;
    host.querySelectorAll("button").forEach((button) => button.classList.toggle("on", button.dataset.source === state.sourceMode));
    const note = detailBody.querySelector("[data-source-note]");
    if (note) note.textContent = state.sourceMode === "live"
      ? "Live proxy · edits appear immediately · candidate only"
      : "Blender 5.2 export · immutable GLB baseline · live additions are not included";
    updateLiveStatus();
  }

  function navigateTo(revision) {
    if (!revision || revision.id === state.visibleRevision?.id || isBusy()) return;
    state.operationId += 1;
    onRevisionChange(revision.id);
  }

  function renderTimeline() {
    const current = state.visibleRevision;
    const host = detailBody.querySelector("[data-visualization-history]");
    if (!host) return;
    const revisions = newestFirstMegastructureRevisions(state.revisions);
    host.innerHTML = `<header><div><b>HISTORY</b><span>${revisions.length} saved version${revisions.length === 1 ? "" : "s"}</span></div><small>Latest first</small></header>
      <div class="mega-history-list">${revisions.map((revision) => {
        const selected = revision.id === current?.id;
        const isHead = revision.id === state.artifact?.workingRevisionId;
        const parent = revisionById(revision.parentRevisionId);
        const branchedFrom = parent && parent.revisionNumber !== revision.revisionNumber - 1 ? ` · from ${revisionLabel(parent)}` : "";
        const edit = revision.body?.edit;
        return `<button type="button" data-history-revision="${esc(revision.id)}" ${selected ? 'aria-current="true"' : ""}>
          <b>${esc(revisionLabel(revision))}</b><span>${esc(edit?.summary || "Blender source")}</span><small>${esc(`${edit?.source || "source"}${branchedFrom}`)}</small>${isHead ? "<em>HEAD</em>" : ""}
        </button>`;
      }).join("")}</div>`;
    host.querySelectorAll("[data-history-revision]").forEach((button) => button.addEventListener("click", () => navigateTo(revisionById(button.dataset.historyRevision))));
  }

  function renderMessages() {
    const host = detailBody.querySelector("[data-visualization-messages]");
    if (!host) return;
    const messages = [...state.revisions].sort((a, b) => a.revisionNumber - b.revisionNumber).filter((revision) => revision.body?.edit);
    host.innerHTML = messages.map((revision) => {
      const edit = revision.body.edit;
      return `<article class="mega-message ${edit.source}"><header><span>${esc(revisionLabel(revision))}</span><b>${esc(edit.source)}</b></header>${edit.request ? `<p class="user">${esc(edit.request)}</p>` : ""}<p>${esc(edit.summary)}</p></article>`;
    }).join("") || `<p class="muted">The first command will appear here.</p>`;
    host.scrollTop = host.scrollHeight;
  }

  function renderControls() {
    const host = detailBody.querySelector("[data-visualization-controls]");
    if (!host) return;
    const parameterControl = ({ label, value, min, max, step, unit = "", scenePath = "", part = "additions", additionId = "", additionField = "" }) => {
      const binding = scenePath
        ? `data-param-path="${esc(scenePath)}" data-param-part="${esc(part)}"`
        : `data-param-addition="${esc(additionId)}" data-param-addition-field="${esc(additionField)}" data-param-part="additions"`;
      return `<label class="mega-param" ${binding} data-param-min="${min}" data-param-max="${max}" data-param-step="${step}">
        <span><b>${esc(label)}</b>${unit ? `<small>${esc(unit)}</small>` : ""}</span>
        <div class="mega-param-editor">
          <button type="button" data-mega-step="-1" aria-label="Decrease ${esc(label)}">−</button>
          <input type="range" data-mega-range min="${min}" max="${max}" step="${step}" value="${esc(value)}" aria-label="${esc(label)} slider">
          <button type="button" data-mega-step="1" aria-label="Increase ${esc(label)}">+</button>
          <input type="number" data-mega-number inputmode="decimal" min="${min}" max="${max}" step="${step}" value="${esc(value)}" aria-label="${esc(label)} exact value">
        </div>
      </label>`;
    };
    const additionCards = (state.scene.additions || []).map((addition) => addition.kind === "radial_tower_array"
      ? `<article><div class="mega-addition-title"><b>${esc(addition.id.replaceAll("_", " "))}</b><small>wall decoration · ${addition.replicationScope === "each_full_stack" ? "every elevator" : "original elevator only"}</small></div>
        ${parameterControl({ label: "Height", value: addition.heightM, min: 1, max: 5_000, step: 1, unit: "m", additionId: addition.id, additionField: "heightM" })}
        ${parameterControl({ label: "Diameter", value: addition.diameterM, min: 1, max: 1_000, step: 1, unit: "m", additionId: addition.id, additionField: "diameterM" })}
        ${parameterControl({ label: "Angular spacing", value: addition.spacingDeg, min: 1, max: 360, step: 1, unit: "°", additionId: addition.id, additionField: "spacingDeg" })}
        ${parameterControl({ label: "Angular offset", value: addition.angularOffsetDeg, min: -360, max: 360, step: 1, unit: "°", additionId: addition.id, additionField: "angularOffsetDeg" })}
        ${parameterControl({ label: "Radial offset", value: addition.radialOffsetM, min: -2_000, max: 2_000, step: 10, unit: "m", additionId: addition.id, additionField: "radialOffsetM" })}
        ${parameterControl({ label: "Base offset", value: addition.baseOffsetM, min: -1_000, max: 5_000, step: 10, unit: "m", additionId: addition.id, additionField: "baseOffsetM" })}
        <label class="mega-select"><span>Elevators</span><select data-mega-addition="${esc(addition.id)}" data-addition-field="replicationScope"><option value="anchor_stack" ${addition.replicationScope === "anchor_stack" ? "selected" : ""}>Original only</option><option value="each_full_stack" ${addition.replicationScope === "each_full_stack" ? "selected" : ""}>Every elevator</option></select></label>
        <label class="mega-select"><span>Colour</span><input type="color" data-mega-addition="${esc(addition.id)}" data-addition-field="color" value="${esc(addition.color)}"></label><button type="button" data-remove-addition="${esc(addition.id)}">Remove</button></article>`
      : `<article class="city"><div class="mega-addition-title"><b>Central Paris / Eiffel</b><small>12 × 10 km · 1 unit = 1 m · schematic, not cadastral GIS</small></div><label class="mega-select"><span>Structure side</span><select data-mega-addition="${esc(addition.id)}" data-addition-field="structureDirection"><option value="east" ${addition.structureDirection === "east" ? "selected" : ""}>East</option><option value="west" ${addition.structureDirection === "west" ? "selected" : ""}>West</option><option value="north" ${addition.structureDirection === "north" ? "selected" : ""}>North</option><option value="south" ${addition.structureDirection === "south" ? "selected" : ""}>South</option></select></label>${parameterControl({ label: "Clearance", value: addition.clearanceM, min: 0, max: 5_000, step: 10, unit: "m", additionId: addition.id, additionField: "clearanceM" })}<button type="button" data-focus-context>View Eiffel</button><button type="button" data-remove-addition="${esc(addition.id)}">Remove</button></article>`).join("");
    const draftSave = describeMegastructureDraftSave(state.draftParts);
    host.innerHTML = `<div class="mega-array-summary"><div><b>${esc(state.scene.structureArray.instanceCount)} sky elevator${state.scene.structureArray.instanceCount === 1 ? "" : "s"}</b><small>${esc(state.scene.tiers.count * state.scene.structureArray.instanceCount)} disk tiers · each cable begins at ground · decorations stay on the original</small></div><button type="button" data-focus-array>View elevators</button></div><div class="mega-field-grid">${FIELDS.map(([part, label, path, min, max, step, unit]) => parameterControl({ label, value: getPath(state.scene, path), min, max, step, unit, scenePath: path, part })).join("")}</div>
      <div class="mega-colours">${Object.entries(state.scene.appearance).map(([key, value]) => `<label><span>${esc(key.replace("Color", ""))}</span><input type="color" data-mega-colour="${esc(key)}" value="${esc(value)}"></label>`).join("")}</div>
      ${additionCards ? `<div class="mega-additions"><header><span>ADDITIONS</span><small>${state.scene.additions.length} typed feature${state.scene.additions.length === 1 ? "" : "s"}</small></header>${additionCards}</div>` : ""}
      <div class="mega-draft-actions"><span data-draft-status>${draftSave.label}</span><button type="button" class="primary" data-save-draft ${draftSave.disabled ? "disabled" : ""}>Save version</button></div>`;
    const markDraft = () => {
      const parts = changedParts(state.baseline, state.scene);
      const { llm: _previousLlm, ...editWithoutLlm } = state.scene.edit;
      state.scene.edit = {
        ...editWithoutLlm,
        source: "manual",
        changedParts: parts,
        request: undefined,
        summary: parts.length ? `Adjusted ${parts.map((part) => part.replaceAll("_", " ")).join(", ")}` : "No changes",
      };
      state.draftParts = parts;
      state.phase = parts.length ? "draft" : "saved";
      const arraySummary = host.querySelector(".mega-array-summary");
      if (arraySummary) {
        arraySummary.querySelector("b").textContent = `${state.scene.structureArray.instanceCount} sky elevator${state.scene.structureArray.instanceCount === 1 ? "" : "s"}`;
        arraySummary.querySelector("small").textContent = `${state.scene.tiers.count * state.scene.structureArray.instanceCount} disk tiers · each cable begins at ground · decorations stay on the original`;
      }
      host.querySelector("[data-draft-status]").textContent = parts.length ? `${parts.length} part${parts.length === 1 ? "" : "s"} changed` : "No unsaved edits";
      host.querySelector("[data-save-draft]").disabled = !parts.length;
      updateLiveStatus();
      queueLiveRender();
    };
    host.querySelectorAll(".mega-param").forEach((control) => {
      const options = {
        min: Number(control.dataset.paramMin),
        max: Number(control.dataset.paramMax),
        step: Number(control.dataset.paramStep),
      };
      const range = control.querySelector("[data-mega-range]");
      const number = control.querySelector("[data-mega-number]");
      const decrease = control.querySelector('[data-mega-step="-1"]');
      const increase = control.querySelector('[data-mega-step="1"]');
      const readValue = () => control.dataset.paramPath
        ? getPath(state.scene, control.dataset.paramPath)
        : state.scene.additions.find((candidate) => candidate.id === control.dataset.paramAddition)?.[control.dataset.paramAdditionField];
      const syncControls = (value) => {
        range.value = String(value);
        number.value = String(value);
        decrease.disabled = Number(value) <= options.min;
        increase.disabled = Number(value) >= options.max;
      };
      const applyValue = (rawValue) => {
        const value = normalizeMegastructureParameter(rawValue, options);
        if (value == null) return;
        const candidateScene = structuredClone(state.scene);
        if (control.dataset.paramPath) {
          setPath(candidateScene, control.dataset.paramPath, value);
        } else {
          const addition = candidateScene.additions.find((candidate) => candidate.id === control.dataset.paramAddition);
          if (!addition) return;
          addition[control.dataset.paramAdditionField] = value;
        }
        try {
          state.scene = validateMegastructureVisualization(candidateScene);
          detailBody.querySelector("[data-visualization-error]")?.replaceChildren();
        } catch (error) {
          syncControls(readValue());
          const target = detailBody.querySelector("[data-visualization-error]");
          if (target) target.textContent = String(error.message || error);
          return;
        }
        if (state.sourceMode !== "live") showLiveProxy();
        syncControls(value);
        markDraft();
      };
      range.addEventListener("input", () => applyValue(range.value));
      number.addEventListener("change", () => {
        if (number.value === "") syncControls(readValue());
        else applyValue(number.value);
      });
      number.addEventListener("keydown", (event) => {
        if (event.key !== "Enter") return;
        event.preventDefault();
        if (number.value === "") syncControls(readValue());
        else applyValue(number.value);
        number.blur();
      });
      control.querySelectorAll("[data-mega-step]").forEach((button) => button.addEventListener("click", () => {
        applyValue(stepMegastructureParameter(readValue(), Number(button.dataset.megaStep), options));
      }));
      syncControls(readValue());
    });
    host.querySelectorAll("[data-mega-colour]").forEach((input) => input.addEventListener("input", () => {
      if (state.sourceMode !== "live") showLiveProxy();
      state.scene.appearance[input.dataset.megaColour] = input.value;
      markDraft();
    }));
    host.querySelectorAll("[data-mega-addition]").forEach((input) => input.addEventListener("input", () => {
      const addition = state.scene.additions.find((candidate) => candidate.id === input.dataset.megaAddition);
      if (!addition) return;
      addition[input.dataset.additionField] = ["color", "select-one"].includes(input.type) ? input.value : Number(input.value);
      markDraft();
    }));
    host.querySelector("[data-focus-context]")?.addEventListener("click", () => viewer.focusGeographicReference());
    host.querySelector("[data-focus-array]")?.addEventListener("click", () => viewer.focusMegastructureArray());
    host.querySelectorAll("[data-remove-addition]").forEach((button) => button.addEventListener("click", () => {
      state.scene.additions = state.scene.additions.filter((addition) => addition.id !== button.dataset.removeAddition);
      button.closest("article")?.remove();
      const count = state.scene.additions.length;
      const summary = host.querySelector(".mega-additions > header small");
      if (summary) summary.textContent = `${count} typed feature${count === 1 ? "" : "s"}`;
      markDraft();
    }));
    host.querySelector("[data-save-draft]").addEventListener("click", async (event) => {
      event.currentTarget.disabled = true;
      state.phase = "saving";
      updateLiveStatus();
      await saveScene(state.scene, {
        parentRevisionId: state.visibleRevision?.id || null,
        expectedHeadRevisionId: state.artifact?.workingRevisionId || null,
      }).catch((error) => {
        state.phase = "save_error";
        updateLiveStatus();
        toast(`Could not save visualization version: ${String(error)}`, true);
        event.currentTarget.disabled = false;
      });
    });
  }

  function renderShell() {
    detailBody.innerHTML = `<div class="mega-studio">
      <header class="mega-studio-head"><div><small>LIVE WORLD VISUALIZER</small><h2>${esc(state.scene.label)}</h2></div><div data-source-switch><button type="button" data-source="live" class="on">Live</button><button type="button" data-source="blender">Blender 5.2</button></div><p data-source-note>Live proxy · edits appear immediately · candidate only</p><div class="mega-live-status" data-live-status data-tone="saved" role="status" aria-live="polite"><i></i><div><b>LIVE</b><span>The viewport matches this saved revision.</span></div></div></header>
      <section data-visualization-history class="mega-history"></section>
      <details class="mega-controls" open><summary>Geometry and colour controls</summary><div data-visualization-controls></div></details>
      <div class="mega-navigation"><label>Tier focus <select data-tier-focus>${Array.from({ length: state.scene.tiers.count }, (_, index) => `<option value="${index}">${String(index + 1).padStart(2, "0")} · ${(index * state.scene.tiers.verticalPitchM / 1000).toFixed(1)} km</option>`).join("")}</select></label><button type="button" data-focus-tier>Go to tier</button><button type="button" data-enter-world>Fly from here</button><button type="button" data-overview>Overview</button><button type="button" data-view-array>View sky elevators</button></div>
      <section data-visualization-messages class="mega-messages" aria-live="polite"></section>
      <p class="mega-error" data-visualization-error></p>
      <form class="mega-composer"><label for="mega-command">Change this world</label><textarea id="mega-command" rows="3" placeholder="Make the pillar wider, reduce it to 18 tiers, and turn the walls dark red…"></textarea><div><small>Next command starts from ${esc(visibleBaseLabel())}, exactly as rendered</small><button type="submit" class="primary">Apply live</button></div></form>
    </div>`;
    renderSourceControls();
    renderTimeline();
    renderControls();
    renderMessages();
    updateLiveStatus();
    detailBody.querySelector('[data-source="live"]').addEventListener("click", showLiveProxy);
    detailBody.querySelector('[data-source="blender"]').addEventListener("click", showBlenderExport);
    detailBody.querySelector("[data-focus-tier]").addEventListener("click", () => {
      if (state.sourceMode !== "live") showLiveProxy();
      viewer.focusStackedMegastructureTier(Number(detailBody.querySelector("[data-tier-focus]").value));
    });
    detailBody.querySelector("[data-enter-world]").addEventListener("click", () => enterMegastructureFlight(viewer));
    detailBody.querySelector("[data-overview]").addEventListener("click", () => {
      viewer.setFlyMode(false);
      viewer.frame();
    });
    detailBody.querySelector("[data-view-array]").addEventListener("click", () => viewer.focusMegastructureArray());
    detailBody.querySelector(".mega-composer").addEventListener("submit", async (event) => {
      event.preventDefault();
      const input = event.currentTarget.querySelector("textarea");
      const button = event.currentTarget.querySelector("button");
      const instruction = input.value.trim();
      if (!instruction) return;
      if (state.sourceMode !== "live") showLiveProxy();
      // Flush any RAF-debounced field edit first. The captured payload is now
      // byte-for-byte the scene most recently handed to the Three.js viewer.
      renderLiveNow(state.scene, { keepCamera: true });
      const operationId = ++state.operationId;
      const baseScene = structuredClone(state.renderedScene);
      const parentRevisionId = state.visibleRevision?.id || null;
      const expectedHeadRevisionId = state.artifact?.workingRevisionId || null;
      state.pendingBaseLabel = visibleBaseLabel();
      state.phase = "thinking";
      button.disabled = true;
      button.textContent = "Thinking…";
      updateLiveStatus();
      try {
        const projectId = getProjectId();
        const response = await api(`/projects/${encodeURIComponent(projectId)}/megastructure-visualization/interpret`, {
          method: "POST",
          body: JSON.stringify({ instruction, scene: baseScene }),
        });
        if (operationId !== state.operationId) return;
        state.phase = "rendering";
        updateLiveStatus();
        state.scene = validateMegastructureVisualization(response.scene);
        state.sourceMode = "live";
        state.draftParts = changedParts(state.baseline, state.scene);
        renderSourceControls();
        renderControls();
        renderLiveNow(state.scene, { keepCamera: true });
        if (state.scene.edit.changedParts.includes("structure_array")) viewer.focusMegastructureArray();
        state.phase = "saving";
        updateLiveStatus();
        await saveScene(state.scene, { parentRevisionId, expectedHeadRevisionId });
        input.value = "";
      } catch (error) {
        if (operationId !== state.operationId) return;
        state.phase = state.phase === "saving" ? "save_error" : "command_error";
        const target = detailBody.querySelector("[data-visualization-error]");
        if (target) target.textContent = String(error).replace(/^Error:\s*/, "");
        updateLiveStatus();
        toast(`Visualization command failed: ${String(error)}`, true);
      } finally {
        button.disabled = false;
        button.textContent = "Apply live";
      }
    });
  }

  async function resolveArtifact() {
    const projectId = getProjectId();
    const listed = await api(`/projects/${encodeURIComponent(projectId)}/artifacts?kind=${encodeURIComponent(ARTIFACT_KIND)}`);
    let artifact = listed.artifacts?.[0] || null;
    if (!artifact) {
      artifact = (await api(`/projects/${encodeURIComponent(projectId)}/artifacts`, {
        method: "POST",
        body: JSON.stringify({ kind: ARTIFACT_KIND, name: "Thirty-Tier Megastructure · Live Visualization" }),
      })).artifact;
    }
    return artifact;
  }

  async function refreshArtifact() {
    const projectId = getProjectId();
    const detailPayload = await api(`/projects/${encodeURIComponent(projectId)}/artifacts/${encodeURIComponent(state.artifact.id)}`);
    state.artifact = detailPayload.artifact;
    state.revisions = detailPayload.revisions || [];
  }

  async function saveScene(scene, {
    parentRevisionId = state.visibleRevision?.id || null,
    expectedHeadRevisionId = state.artifact?.workingRevisionId || null,
    retry = true,
  } = {}) {
    const projectId = getProjectId();
    const parsed = validateMegastructureVisualization(scene);
    try {
      const created = await api(`/projects/${encodeURIComponent(projectId)}/artifacts/${encodeURIComponent(state.artifact.id)}/revisions`, {
        method: "POST",
        body: JSON.stringify({
          body: parsed,
          parentRevisionId,
          expectedHeadRevisionId,
          changeNote: parsed.edit.summary,
          validation: { schema: parsed.schemaVersion, projection: "threejs_candidate", blenderVersion: "5.2.0" },
        }),
      });
      await refreshArtifact();
      state.visibleRevision = revisionById(created.revision.id) || created.revision;
      state.scene = structuredClone(parsed);
      state.renderedScene = structuredClone(parsed);
      state.baseline = structuredClone(parsed);
      state.draftParts = [];
      state.phase = "saved";
      state.pendingBaseLabel = "";
      updateLiveStatus();
      toast(`${revisionLabel(created.revision)} saved${parentRevisionId && parentRevisionId !== expectedHeadRevisionId ? " as a branch from the visible version" : ""}`);
      await onRevisionChange(created.revision.id);
      return created.revision;
    } catch (error) {
      if (!retry || !String(error).includes("artifact changed")) throw error;
      await refreshArtifact();
      return saveScene(parsed, {
        parentRevisionId,
        expectedHeadRevisionId: state.artifact?.workingRevisionId || null,
        retry: false,
      });
    }
  }

  async function activate({ revisionId = "" } = {}) {
    state.operationId += 1;
    state.active = true;
    state.loading = true;
    sidebar.style.display = "none";
    detail.classList.add("mega-detail");
    try {
      state.artifact = await resolveArtifact();
      await refreshArtifact();
      if (!state.revisions.length) {
        state.scene = cloneDefaultMegastructureVisualization();
        state.visibleRevision = null;
        await saveScene(state.scene);
        return;
      }
      state.visibleRevision = revisionById(revisionId) || revisionById(state.artifact.workingRevisionId) || state.revisions[0];
      state.scene = validateMegastructureVisualization(state.visibleRevision.body);
      state.baseline = structuredClone(state.scene);
      state.renderedScene = null;
      state.draftParts = [];
      state.phase = "saved";
      state.pendingBaseLabel = "";
      state.sourceMode = "live";
      renderShell();
      queueLiveRender({ keepCamera: !state.firstLoad });
      if (!revisionId || revisionId !== state.visibleRevision.id) onRevisionChange(state.visibleRevision.id, { replace: true });
    } catch (error) {
      state.scene = cloneDefaultMegastructureVisualization();
      state.baseline = structuredClone(state.scene);
      state.visibleRevision = null;
      state.draftParts = [];
      state.phase = "offline_error";
      renderShell();
      queueLiveRender({ keepCamera: false });
      toast(`Live history is offline; showing the local Blender 5.2 specification. ${String(error)}`, true);
    } finally {
      state.loading = false;
    }
  }

  function deactivate() {
    if (state.active) state.cameraPose = viewer.captureCameraPose();
    state.active = false;
    state.operationId += 1;
    state.firstLoad = true;
    detail.classList.remove("mega-detail");
  }

  return { activate, deactivate, get active() { return state.active; } };
}
